import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import axiosInstance from 'ns_libs/nsAxios';
import API_ENDPOINTS from 'services/urls';
import { SensitivityAnalysisResponse } from '../types';

export interface SensitivityTaskStatusResponse {
    taskId: string;
    status: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE';
    message: string;
    result?: SensitivityAnalysisResponse;
    currentStep?: number;
    error?: string;
    timedOut?: boolean;
}

export const MAX_POLLING_DURATION_MS = 5 * 60 * 1_000;
const POLLING_INTERVAL_MS = 3 * 1_000;

const fetchTaskStatus = async (
    taskId: string,
    organizationId: string,
): Promise<SensitivityTaskStatusResponse> => {
    const response = await axiosInstance.get(API_ENDPOINTS.TASKS.TASK_STATUS(taskId), {
        params: { organizationId },
    });
    return response.data;
};

interface UseSensitivityTaskStatusOptions {
    taskId?: string | null;
    organizationId: string;
    enabled?: boolean;
}

/** Polls a sensitivity task's status until it succeeds, fails, or exceeds MAX_POLLING_DURATION_MS. */
const useSensitivityTaskStatus = ({
    taskId,
    organizationId,
    enabled = true,
}: UseSensitivityTaskStatusOptions) => {
    const queryClient = useQueryClient();
    const shouldPoll = enabled && !!taskId;
    const pollingStartTimeRef = useRef<number | null>(null);

    useEffect(() => {
        if (shouldPoll) {
            pollingStartTimeRef.current = Date.now();
        }
        return () => {
            if (!shouldPoll) {
                pollingStartTimeRef.current = null;
            }
        };
    }, [shouldPoll, taskId]);

    return useQuery({
        queryKey: ['sensitivityTaskStatus', taskId, organizationId],
        queryFn: () => fetchTaskStatus(taskId!, organizationId),
        enabled: shouldPoll,
        refetchInterval: query => {
            if (!taskId) return false;
            if (query.state.status === 'error') return false;

            const { data } = query.state;
            if (data?.status === 'SUCCESS' || data?.status === 'FAILURE') return false;

            const elapsedTimeMs = pollingStartTimeRef.current != null
                ? Date.now() - pollingStartTimeRef.current
                : null;

            if (elapsedTimeMs != null && elapsedTimeMs > MAX_POLLING_DURATION_MS) {
                const sentinel: SensitivityTaskStatusResponse = {
                    taskId,
                    status: 'FAILURE',
                    message: 'Polling timed out',
                    error: 'Polling exceeded maximum duration',
                    timedOut: true,
                };
                queryClient.setQueryData(
                    ['sensitivityTaskStatus', taskId, organizationId],
                    sentinel,
                );
                console.warn(
                    '[SensitivityTaskStatus] polling exceeded maximum duration. Stopping.',
                    {
                        taskId,
                        elapsedTimeMs,
                        elapsedTimeSeconds: Math.round(elapsedTimeMs / 1_000),
                        maxDurationMs: MAX_POLLING_DURATION_MS,
                        maxDurationMinutes: MAX_POLLING_DURATION_MS / 60_000,
                    },
                );
                return false;
            }

            return POLLING_INTERVAL_MS;
        },
        retry: 3,
        retryDelay: 1_000,
    });
};

export default useSensitivityTaskStatus;
